/**
 * custom-copyable.js
 * CCC — sleek click-to-copy for product title, price, and variant info.
 * Scoped to `.product-details` so product cards keep normal link behaviour.
 *
 * Title text copies the title only.
 * Title side button copies: "{title} - {price} | {variantUrl}"
 */

import { ThemeEvents } from '@theme/events';

const COPY_ICON = `
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.25" fill="none" stroke="currentColor" stroke-width="1.25"/>
    <path d="M3.5 10.5h-1A1.5 1.5 0 0 1 1 9V2.5A1.5 1.5 0 0 1 2.5 1H9a1.5 1.5 0 0 1 1.5 1.5v1" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>
  </svg>
`.trim();

/**
 * @param {string} text
 * @returns {Promise<boolean>}
 */
async function writeClipboard(text) {
  if (!text || text === 'N/A') return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }

  try {
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    const ok = document.execCommand('copy');
    input.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * @param {HTMLElement} tip
 * @param {string} label
 */
function setTipLabel(tip, label) {
  const text = tip.querySelector('.ccc-copy__tip-text');
  if (text) {
    text.textContent = label;
    return;
  }

  tip.textContent = label;
}

/**
 * @param {string} label
 * @param {string} [className='ccc-copy__tip']
 * @returns {HTMLSpanElement}
 */
function createTip(label, className = 'ccc-copy__tip') {
  const tip = document.createElement('span');
  tip.className = className;
  tip.setAttribute('aria-hidden', 'true');

  const icon = document.createElement('span');
  icon.className = 'ccc-copy__tip-icon';
  icon.innerHTML = COPY_ICON;

  const text = document.createElement('span');
  text.className = 'ccc-copy__tip-text';
  text.textContent = label;

  tip.append(icon, text);
  return tip;
}

/** @type {HTMLElement | null} */
let copyAnnouncer = null;

/**
 * Screen reader "Copied" message. The copy hosts carry no aria-label, so the
 * product title and SKU values are read as themselves, not as "Copy title".
 * @param {string} message
 */
function announce(message) {
  if (!copyAnnouncer || !copyAnnouncer.isConnected) {
    copyAnnouncer = document.createElement('span');
    copyAnnouncer.className = 'visually-hidden';
    copyAnnouncer.setAttribute('aria-live', 'polite');
    document.body.appendChild(copyAnnouncer);
  }
  copyAnnouncer.textContent = '';
  window.setTimeout(() => {
    if (copyAnnouncer) copyAnnouncer.textContent = message;
  }, 50);
}

/**
 * @param {HTMLElement} host
 * @param {string} label
 */
function showCopied(host, label) {
  host.dataset.copied = 'true';
  const tip = host.querySelector('.ccc-copy__tip, .ccc-title-copy__tip');
  if (tip) setTipLabel(tip, 'Copied');
  announce(`Copied ${label}`);

  window.setTimeout(() => {
    delete host.dataset.copied;
    if (tip) setTipLabel(tip, host.dataset.tipDefault || 'Copy');
  }, 1400);
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isAlreadyEnhanced(el) {
  return el.classList.contains('ccc-copy') || Boolean(el.querySelector('.ccc-copy'));
}

/**
 * @param {HTMLElement} details
 * @returns {string}
 */
function getCurrentPriceText(details) {
  const priceRoot = details.querySelector('product-price');
  if (!(priceRoot instanceof HTMLElement)) return '';

  const priceGroup =
    priceRoot.querySelector('.ccc-copy--price .ccc-copy__value') ||
    priceRoot.querySelector('.price-item__group.price:not(.compare-at-price)') ||
    priceRoot.querySelector('span.price:not(.compare-at-price *)');

  if (!(priceGroup instanceof HTMLElement)) return '';

  const clone = priceGroup.cloneNode(true);
  if (clone instanceof HTMLElement) {
    clone.querySelectorAll('.visually-hidden, .ccc-copy__tip').forEach((node) => node.remove());
  }

  return (clone.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * @param {HTMLElement} details
 * @returns {string}
 */
function getVariantUrl(details) {
  const section = details.closest('.shopify-section') || document;
  const idInput =
    section.querySelector('product-form input[name="id"]') ||
    section.querySelector('form[action*="/cart/add"] input[name="id"]') ||
    document.querySelector('product-form input[name="id"]') ||
    document.querySelector('form[action*="/cart/add"] input[name="id"]');

  const variantId = idInput instanceof HTMLInputElement ? idInput.value : '';
  const url = new URL(window.location.href);
  return variantId
    ? `${url.origin}${url.pathname}?variant=${variantId}`
    : `${url.origin}${url.pathname}`;
}

/**
 * @param {HTMLElement} details
 * @param {string} title
 * @returns {string}
 */
function buildTitleBundle(details, title) {
  const price = getCurrentPriceText(details);
  const productUrl = getVariantUrl(details);
  if (price) return `${title} - ${price} | ${productUrl}`;
  return `${title} | ${productUrl}`;
}

/**
 * @param {HTMLElement} heading
 * @returns {string}
 */
function getHeadingTitleText(heading) {
  const value = heading.querySelector('.ccc-copy--title .ccc-copy__value');
  if (value instanceof HTMLElement) {
    return (value.textContent || '').replace(/\s+/g, ' ').trim();
  }

  return Array.from(heading.childNodes)
    .filter(
      (node) =>
        node.nodeType === Node.TEXT_NODE ||
        (node instanceof HTMLElement &&
          !node.classList.contains('ccc-title-copy') &&
          !node.classList.contains('ccc-copy'))
    )
    .map((node) => node.textContent || '')
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Wrap an element's contents in a copyable control.
 * @param {HTMLElement} el
 * @param {{ text: string, label: string, block?: boolean, extraClass?: string }} options
 */
function wrapElement(el, { text, label, block = false, extraClass = '' }) {
  if (!(el instanceof HTMLElement) || isAlreadyEnhanced(el)) return;
  if (!text || text === 'N/A') return;

  const host = document.createElement('span');
  host.className = `ccc-copy${block ? ' ccc-copy--block' : ''}${extraClass ? ` ${extraClass}` : ''}`;
  host.tabIndex = 0;
  host.setAttribute('role', 'button');
  host.dataset.copyLabel = label;
  host.dataset.cccCopy = text;
  host.dataset.tipDefault = 'Copy';

  const value = document.createElement('span');
  value.className = 'ccc-copy__value';

  while (el.firstChild) {
    value.appendChild(el.firstChild);
  }

  host.append(value, createTip('Copy'));
  el.appendChild(host);
}

/**
 * @param {HTMLElement|null} el
 * @param {string} label
 */
function enhanceValueEl(el, label) {
  if (!(el instanceof HTMLElement) || isAlreadyEnhanced(el)) return;

  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (!text || text === 'N/A') return;

  const host = document.createElement('span');
  host.className = 'ccc-copy';
  host.tabIndex = 0;
  host.setAttribute('role', 'button');
  host.dataset.copyLabel = label;
  host.dataset.cccCopy = text;
  host.dataset.tipDefault = 'Copy';

  const value = document.createElement('span');
  value.className = 'ccc-copy__value';
  value.textContent = text;

  host.append(value, createTip('Copy'));
  el.replaceChildren(host);
}

/** @type {WeakMap<HTMLElement, ResizeObserver>} */
const titleButtonObservers = new WeakMap();

/**
 * First line box of the title text.
 * getBoundingClientRect() unions all wrapped lines, which parks the button
 * at the trailing edge of the whole block (often the last line).
 * @param {HTMLElement} value
 * @returns {DOMRect | null}
 */
function getTitleFirstLineRect(value) {
  const lineRects = value.getClientRects();
  if (lineRects.length > 0) return lineRects[0];

  const fallback = value.getBoundingClientRect();
  if (fallback.width === 0 && fallback.height === 0) return null;
  return fallback;
}

/**
 * Pin the secret button just after the first line of title text.
 * Vertically centres on that line so multi-line titles do not leave a gap.
 * @param {HTMLElement} heading
 * @param {HTMLElement} button
 */
function positionTitleButton(heading, button) {
  const value = heading.querySelector('.ccc-copy--title .ccc-copy__value');
  if (!(value instanceof HTMLElement)) return;

  const headingRect = heading.getBoundingClientRect();
  const firstLine = getTitleFirstLineRect(value);
  if (!firstLine) return;

  const gap = 6;
  const buttonHeight = button.offsetHeight || parseFloat(getComputedStyle(button).height) || 0;
  const lineMid = (firstLine.top + firstLine.bottom) / 2;
  const top = Math.max(0, lineMid - headingRect.top - buttonHeight / 2);
  const left = Math.max(0, firstLine.right - headingRect.left + gap);

  button.style.top = `${top}px`;
  button.style.left = `${left}px`;
  button.style.right = 'auto';
  button.style.bottom = 'auto';
}

/**
 * Keep the secret button glued to the first line across reflow (resize, fonts, column width).
 * @param {HTMLElement} heading
 * @param {HTMLElement} button
 */
function observeTitleButtonLayout(heading, button) {
  if (titleButtonObservers.has(heading)) return;

  const schedule = () => {
    // A DOM morph can replace the heading wholesale; stop observing the
    // detached node instead of resizing into the void until page unload.
    if (!heading.isConnected) {
      const stale = titleButtonObservers.get(heading);
      if (stale) stale.disconnect();
      titleButtonObservers.delete(heading);
      return;
    }
    requestAnimationFrame(() => {
      const current =
        heading.querySelector(':scope > .ccc-title-copy') ||
        (button.isConnected ? button : null);
      if (current instanceof HTMLElement) positionTitleButton(heading, current);
    });
  };

  const observer = new ResizeObserver(schedule);
  observer.observe(heading);

  const value = heading.querySelector('.ccc-copy--title .ccc-copy__value');
  if (value instanceof HTMLElement) observer.observe(value);

  titleButtonObservers.set(heading, observer);
}

/**
 * Title text copies the title only.
 * Side button (hover) copies: Title - $Price | variant URL
 * @param {HTMLElement} details
 */
function enhanceTitle(details) {
  const heading = details.querySelector('h1');
  if (!(heading instanceof HTMLElement)) return;

  heading.classList.add('ccc-title-copy-host');

  let titleHost = heading.querySelector('.ccc-copy--title');
  if (!(titleHost instanceof HTMLElement)) {
    titleHost = document.createElement('span');
    titleHost.className = 'ccc-copy ccc-copy--title';
    titleHost.tabIndex = 0;
    titleHost.setAttribute('role', 'button');
    titleHost.dataset.copyLabel = 'title';
    titleHost.dataset.tipDefault = 'Copy';

    const value = document.createElement('span');
    value.className = 'ccc-copy__value';

    const moveNodes = Array.from(heading.childNodes).filter(
      (node) =>
        !(node instanceof HTMLElement &&
          (node.classList.contains('ccc-title-copy') || node.classList.contains('ccc-copy--title')))
    );

    for (const node of moveNodes) {
      value.appendChild(node);
    }

    titleHost.append(value, createTip('Copy'));
    heading.replaceChildren(titleHost);
  }

  // The secret side button is a staff control, invisible at opacity 0 with an
  // enlarged hit pad. On touch screens it sat past the end of the title where
  // stray taps silently wrote "Title - $Price | URL" to a customer's
  // clipboard (and poked past the viewport on long titles) — so it only
  // exists for precise hover pointers.
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const stale = heading.querySelector('.ccc-title-copy');
    if (stale) stale.remove();
    const title = getHeadingTitleText(heading);
    if (title) titleHost.dataset.cccCopy = title;
    return;
  }

  let button = heading.querySelector(':scope > .ccc-title-copy') || heading.querySelector('.ccc-title-copy');
  if (!(button instanceof HTMLButtonElement)) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'ccc-title-copy';
    // Mouse-only staff control inside the h1. Hidden from assistive tech and
    // the tab order so it doesn't add "Copy title, price and link" to the
    // heading's accessible name.
    button.tabIndex = -1;
    button.setAttribute('aria-hidden', 'true');
    button.dataset.tipDefault = 'Copy + price + URL';

    const icon = document.createElement('span');
    icon.className = 'ccc-title-copy__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = COPY_ICON;

    button.append(icon, createTip('Copy + price + URL', 'ccc-title-copy__tip'));
  }

  // Keep as direct h1 child so absolute coords are relative to the heading
  if (button.parentElement !== heading) {
    heading.appendChild(button);
  }

  const title = getHeadingTitleText(heading);
  if (!title) return;

  titleHost.dataset.cccCopy = title;
  button.dataset.cccCopy = buildTitleBundle(details, title);
  positionTitleButton(heading, button);
  observeTitleButtonLayout(heading, button);

  // Fonts / late layout can change wrap points after first paint
  requestAnimationFrame(() => positionTitleButton(heading, button));
}

/**
 * @param {HTMLElement} details
 */
function enhancePrice(details) {
  const priceRoot = details.querySelector('product-price');
  if (!(priceRoot instanceof HTMLElement)) return;

  const priceGroup =
    priceRoot.querySelector('.price-item__group.price:not(.compare-at-price)') ||
    priceRoot.querySelector('span.price:not(.compare-at-price *)');

  if (!(priceGroup instanceof HTMLElement) || isAlreadyEnhanced(priceGroup)) return;

  const clone = priceGroup.cloneNode(true);
  if (clone instanceof HTMLElement) {
    clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
  }
  const text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
  if (!text || text === '\u00a0') return;

  wrapElement(priceGroup, { text, label: 'price', block: true, extraClass: 'ccc-copy--price' });
}

/**
 * @param {HTMLElement} details
 */
function enhanceVariantInfo(details) {
  const map = [
    ['[data-cvi-sku]', 'SKU'],
    ['[data-cvi-barcode]', 'barcode'],
    ['[data-cvi-weight]', 'shipping weight'],
    ['[data-cvi-dimensions]', 'shipping dimensions'],
    ['[data-cvi-bin]', 'warehouse location'],
  ];

  for (const [selector, label] of map) {
    const el = details.querySelector(selector);
    if (el instanceof HTMLElement) enhanceValueEl(el, label);
  }
}

/**
 * @param {ParentNode} [root]
 */
export function initProductCopyables(root = document) {
  const details = root.querySelector('.product-details');
  if (!(details instanceof HTMLElement)) return;

  enhancePrice(details);
  enhanceVariantInfo(details);
  // Title last so it can read the enhanced price text
  enhanceTitle(details);
}

/**
 * @param {Event} event
 */
async function onActivate(event) {
  const host =
    event.target instanceof Element
      ? event.target.closest('.ccc-copy, .ccc-title-copy')
      : null;
  if (!(host instanceof HTMLElement)) return;
  if (!host.closest('.product-details')) return;

  event.preventDefault();
  event.stopPropagation();

  // Refresh title payloads at click time so variant/price are current
  if (host.classList.contains('ccc-title-copy') || host.classList.contains('ccc-copy--title')) {
    const details = host.closest('.product-details');
    const heading = host.closest('h1');
    if (details instanceof HTMLElement && heading instanceof HTMLElement) {
      const title = getHeadingTitleText(heading);
      if (title) {
        if (host.classList.contains('ccc-title-copy')) {
          host.dataset.cccCopy = buildTitleBundle(details, title);
        } else {
          host.dataset.cccCopy = title;
        }
      }
    }
  }

  const text = host.dataset.cccCopy || host.querySelector('.ccc-copy__value')?.textContent?.trim() || '';
  const label = host.classList.contains('ccc-title-copy')
    ? 'title, price and link'
    : host.dataset.copyLabel || 'value';

  const ok = await writeClipboard(text);
  if (ok) showCopied(host, label || 'value');
}

function onKeydown(event) {
  if (!(event instanceof KeyboardEvent)) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const host =
    event.target instanceof Element
      ? event.target.closest('.ccc-copy, .ccc-title-copy')
      : null;
  if (!(host instanceof HTMLElement) || event.target !== host) return;

  event.preventDefault();
  onActivate(event);
}

function repositionOpenTitleButtons() {
  for (const heading of document.querySelectorAll('.product-details h1.ccc-title-copy-host')) {
    if (!(heading instanceof HTMLElement)) continue;
    const button = heading.querySelector(':scope > .ccc-title-copy');
    if (button instanceof HTMLElement) positionTitleButton(heading, button);
  }
}

function bind() {
  document.addEventListener('click', onActivate);
  document.addEventListener('keydown', onKeydown);

  initProductCopyables();

  document.addEventListener(ThemeEvents.variantUpdate, () => {
    setTimeout(() => initProductCopyables(), 0);
  });

  document.addEventListener(ThemeEvents.variantSelected, () => {
    setTimeout(() => initProductCopyables(), 0);
  });

  document.addEventListener('ccc:variant-info-updated', () => {
    initProductCopyables();
  });

  // Horizon hydration can restore the server DOM without firing any variant
  // event, stripping the injected copy wrappers until the next variant
  // change. Re-init (idempotent) once the mutation burst settles; the
  // re-init only mutates when wrappers are actually missing, so this
  // cannot loop.
  const details = document.querySelector('.product-details');
  if (details) {
    let rebindTimer = null;
    new MutationObserver(() => {
      clearTimeout(rebindTimer);
      rebindTimer = setTimeout(() => initProductCopyables(), 120);
    }).observe(details, { childList: true, subtree: true });
  }

  window.addEventListener('resize', repositionOpenTitleButtons, { passive: true });
  window.visualViewport?.addEventListener('resize', repositionOpenTitleButtons, { passive: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => repositionOpenTitleButtons()).catch(() => {});
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bind, { once: true });
} else {
  bind();
}
